"use client";

/* -----------------Components--------------- */
import type { IconName } from "@/components/Icon";
import ItemIcon from "@/components/ItemIcon";
import {
	NavigationMenu,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
} from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { useNavbar } from "@/context/NavbarContext";
import { cn } from "@/lib/utils";
import { stegaClean } from "next-sanity";

/* -----------------Helpers--------------- */
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type Props = {
	items: {
		name: string;
		href: string;
		icon?: IconName;
		iconUrl?: string;
		isImage?: boolean;
	}[];
};

const customTriggerStyle = () =>
	"group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent focus:outline-hidden disabled:pointer-events-none disabled:opacity-50 data-active:bg-primary data-[state=open]:bg-accent data-[state=open]:text-foreground";

const SubNavbar = ({ items }: Props) => {
	const pathname = usePathname();
	const [containerElement, setContainerElement] = useState<HTMLElement | null>(
		null,
	);

	const { scrolled } = useNavbar();

	// Effect to find the portal container
	useEffect(() => {
		// Look for the portal container
		const container = document.getElementById("subnav-container");
		setContainerElement(container);
	}, []);

	// The navigation content to render
	const navContent = (
		<>
			<NavigationMenu
				className={cn("flex w-full max-w-none justify-start px-8 py-2", {
					hidden: !containerElement,
					"bg-transparent": !scrolled,
					"bg-white": scrolled,
				})}
			>
				<NavigationMenuList className="flex items-center">
					{items.map((item) => {
						const stegaClearedHref = stegaClean(item.href);
						const isActive = pathname === stegaClearedHref;

						return (
							<NavigationMenuItem key={item.name}>
								<NavigationMenuLink
									asChild
									className={cn(customTriggerStyle(), {
										"text-primary": isActive,
									})}
								>
									<Link
										href={stegaClearedHref}
										className="flex flex-row items-center gap-2"
									>
										<ItemIcon
											icon={item.icon}
											iconUrl={item.iconUrl}
											alt={item.name}
											isImage={item.isImage}
											width={24}
											height={24}
											className="w-4"
										/>
										{item.name}
									</Link>
								</NavigationMenuLink>
							</NavigationMenuItem>
						);
					})}
				</NavigationMenuList>
			</NavigationMenu>
			<Separator className="bg-black/5" />
		</>
	);

	// If we don't have the container element yet or something went wrong,
	// render the navigation in its original position as a fallback
	if (!containerElement) {
		return <nav className="hidden lg:block">{navContent}</nav>;
	}

	// If we have the container, create a portal to it
	return createPortal(
		<nav className="hidden lg:block">{navContent}</nav>,
		containerElement,
	);
};

export default SubNavbar;
